import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Domain as DomainIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useLoginMutation, useMfaVerifyMutation } from '@/store/api/authApi';
import { useAppSelector, useAppDispatch } from '@/store';
import { benutzerAusToken } from '@/store/slices/authSlice';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useAppDispatch();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [mfaVerify, { isLoading: isVerifyingMfa }] = useMfaVerifyMutation();
  const mfaPending = useAppSelector((state) => state.auth.mfaPending);

  const [tenantDomain, setTenantDomain] = useState(
    localStorage.getItem('tenantDomain') || ''
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login({
        email,
        password,
        tenantDomain: tenantDomain || undefined,
      }).unwrap();

      if (result.mfaRequired) {
        setTempToken(result.accessToken);
      } else {
        // Die Person aus dem Token — wie beim Neuladen der Seite. Hier stand ein
        // fester Vorname „Admin“: Kopfzeile und vorbelegte Felder zeigten ihn
        // für jede Person, bis die Seite neu geladen wurde (Testgrundlage 2.2,
        // B-02). Der Name kommt aus dem eigenen Profil (Kopfzeile).
        const ausToken = benutzerAusToken(result.accessToken);

        // Store in localStorage directly (simple and reliable)
        localStorage.setItem('accessToken', result.accessToken);
        localStorage.setItem('refreshToken', result.refreshToken);
        if (tenantDomain) localStorage.setItem('tenantDomain', tenantDomain);

        // Dispatch plain action object directly
        dispatch({
          type: 'auth/setCredentials',
          payload: {
            user: {
              id: ausToken?.id ?? '',
              email: ausToken?.email || email,
              firstName: ausToken?.firstName ?? '',
              lastName: ausToken?.lastName ?? '',
              roles: ausToken?.roles ?? [],
              permissions: ausToken?.permissions ?? [],
              tenantId: ausToken?.tenantId ?? '',
              tenantName: tenantDomain,
              mfaEnabled: false,
            },
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          },
        });
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const message =
        err?.data?.message ||
        err?.message ||
        (typeof err === 'string' ? err : 'Login fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setError(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await mfaVerify({ code: mfaCode, tempToken }).unwrap();
      navigate('/', { replace: true });
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        t('errors.generic');
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleMfaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setMfaCode(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f2137 0%, #1e3a5f 40%, #3b6cb5 100%)',
        position: 'relative',
        p: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(ellipse at 30% 20%, rgba(59, 108, 181, 0.3) 0%, transparent 60%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          borderRadius: '10px',
          overflow: 'visible',
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.92)'
              : 'rgba(17, 27, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          border: (theme) =>
            theme.palette.mode === 'light'
              ? '1px solid #dce1e8'
              : '1px solid #1e3354',
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 1px 3px rgba(0,0,0,0.06), 0 8px 30px rgba(0,0,0,0.08)'
              : '0 4px 24px rgba(0,0,0,0.4)',
        }}
        elevation={0}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo / Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #1e3a5f, #3b6cb5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
                boxShadow: '0 4px 16px rgba(30, 58, 95, 0.25)',
              }}
            >
              <SecurityIcon sx={{ fontSize: 32, color: '#ffffff' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'light' ? '#1e3a5f' : '#e4e9f1',
                letterSpacing: '-0.01em',
              }}
            >
              {t('app.title')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: (theme) => theme.palette.mode === 'light' ? '#5a6978' : '#8899ad' }}>
              {t('app.subtitle')}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3, borderColor: (theme) => theme.palette.mode === 'light' ? '#dce1e8' : '#1e3354' }} />

          {!mfaPending ? (
            /* Login Form */
            <Box component="form" onSubmit={handleLogin} noValidate>
              <Typography variant="h6" gutterBottom>
                {t('auth.loginTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('auth.loginSubtitle')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label={t('auth.tenantDomain')}
                value={tenantDomain}
                onChange={(e) => setTenantDomain(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                placeholder="company.revisionssoftware.de"
                size="medium"
              />

              <TextField
                fullWidth
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                autoComplete="email"
                size="medium"
              />

              <TextField
                fullWidth
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete="current-password"
                size="medium"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoggingIn || !email || !password}
                sx={{
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  bgcolor: '#1e3a5f',
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: '#162d4a',
                    boxShadow: '0 4px 16px rgba(30, 58, 95, 0.25)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isLoggingIn ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('auth.login')
                )}
              </Button>
            </Box>
          ) : (
            /* MFA Verification Step */
            <Box component="form" onSubmit={handleMfaVerify} noValidate>
              <Typography variant="h6" gutterBottom>
                {t('auth.mfaCode')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label={t('auth.mfaCode')}
                value={mfaCode}
                onChange={handleMfaCodeChange}
                required
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SecurityIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: {
                    letterSpacing: '0.5em',
                    fontSize: '1.25rem',
                    textAlign: 'center',
                  },
                }}
                placeholder="000000"
                autoFocus
                size="medium"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isVerifyingMfa || mfaCode.length !== 6}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {isVerifyingMfa ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('actions.confirm')
                )}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
